import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';

import ConfirmModalMUI from '../warehouse/modals/ConfirmModalMUI';
import FeedbackModalMUI from '../warehouse/modals/FeedbackModalMUI';
import { handleManifestoPrint, fetchManifesto } from '../services/api';
import axios from 'axios';

const ManifestosImp = ({ open, onClose }) => {
  const [date, setDate] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');

  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [loadingManifesto, setLoadingManifesto] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // cleanup do object URL
  useEffect(() => () => {
    if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
  }, [pdfBlobUrl]);

  // sempre que abrir ou mudar data, tenta carregar
  useEffect(() => {
    if (open && date) {
      loadManifesto();
    } else {
      setPdfBlobUrl(null);
      setNotFound(false);
    }
  }, [open, date]);

  const loadManifesto = async () => {
    setLoadingManifesto(true);
    setNotFound(false);
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }

    try {
      // 1) Pega registro para saber o URL do PDF
      const { data } = await fetchManifesto(date);
      const pdfUrl = data.pdf; // vem do serializer: URL completa

      // 2) Baixa como blob
      const resp = await axios.get(pdfUrl, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      setPdfBlobUrl(url);

    } catch (err) {
      if (err.response?.status === 404) {
        setNotFound(true);
      } else {
        console.error(err);
      }
    } finally {
      setLoadingManifesto(false);
    }
  };

  const handlePrintClick = () => setConfirmOpen(true);

  const handleConfirm = async () => {
    setConfirmOpen(false);
    setFeedbackOpen(true);
    setIsSubmitting(true);

    try {
      const { data } = await handleManifestoPrint({ date });
      setFeedbackMessage(data.message || 'Manifesto gerado');
      // regenera o blob
      await loadManifesto();
    } catch (error) {
      console.error(error);
      setFeedbackMessage('Erro ao gerar manifesto');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmCancel = () => setConfirmOpen(false);
  const handleFeedbackClose = () => {
    setFeedbackOpen(false);
    setFeedbackMessage('');
    // Não fecha a modal principal automaticamente - usuário controla quando fechar
  };

  const renderPreview = () => {
    if (!date) return null;
    if (loadingManifesto) {
      return <Box textAlign="center" mt={2}><CircularProgress /></Box>;
    }
    if (notFound) {
      return (
        <Box mt={2}>
          <Typography>Nenhum manifesto gerado para {date}.</Typography>
        </Box>
      );
    }
    if (pdfBlobUrl) {
      return (
        <Box mt={2}>
          <Typography variant="subtitle1" gutterBottom>Visualização do manifesto:</Typography>
          <iframe
            src={pdfBlobUrl}
            title="PDF Preview"
            width="100%"
            height="500px"
            style={{ border: 'none' }}
          />
        </Box>
      );
    }
    return null;
  };

  return (
    <>
      <Dialog open={open} onClose={() => {}} fullWidth maxWidth="sm">
        <DialogTitle>Gerar/Atualizar Manifesto</DialogTitle>
        <DialogContent>
          <TextField
            label="Data"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={date}
            onChange={e => setDate(e.target.value)}
            required
          />
          {renderPreview()}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="contained" onClick={handlePrintClick} disabled={!date}>
            Gerar/Atualizar
          </Button>
          {/* Botão para fechar manualmente após gerar manifesto */}
          {pdfBlobUrl && (
            <Button onClick={onClose} color="primary" variant="outlined">
              Fechar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ConfirmModalMUI
        open={confirmOpen}
        message="Deseja realmente gerar/atualizar o manifesto para esta data?"
        onConfirm={handleConfirm}
        onCancel={handleConfirmCancel}
      />

      <FeedbackModalMUI
        open={feedbackOpen}
        isSubmitting={isSubmitting}
        submissionComplete={!isSubmitting && !!feedbackMessage}
        error={feedbackMessage.startsWith('Erro') ? feedbackMessage : null}
        feedbackMessage={feedbackMessage}
        onClose={handleFeedbackClose}
      />
    </>
  );
};

export default ManifestosImp;
